import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useUserStore } from "../../stores/UserStore";
import { getProfile } from "../../backend/api/auth";
import { User, Contact } from "../../types/User";
import EditProfileModal from "../EditProfileModal";
import ContactSelectorModal from "../ContactSelectorModal";
import UserAddressForm from "../../pages/Profilo/UserAddressForm";
import { GearPal } from "../../types/Pal";

type ModalId = "editProfile" | "userAddress" | string;

type ModalsState = Record<ModalId, boolean>;

type ModalsContextValue = {
  isOpen: (id: ModalId) => boolean;
  open: (id: ModalId) => void;
  close: (id: ModalId) => void;
  toggle: (id: ModalId) => void;
  openContactSelector: (contacts: Contact[], type: Contact["type"]) => void;
  openUserAddress: (
    isEdit?: boolean,
    palToEdit?: GearPal | null,
    onSuccess?: (pal?: GearPal) => void
  ) => void;
};

export const ModalsContext = createContext<ModalsContextValue | undefined>(
  undefined
);

export const ModalsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [modals, setModals] = useState<ModalsState>({});
  const [contactSelectorState, setContactSelectorState] = useState<{
    contacts: Contact[];
    type: Contact["type"];
  } | null>(null);
  const [userAddressState, setUserAddressState] = useState<{
    isEdit: boolean;
    palToEdit?: GearPal | null;
    onSuccess?: (pal?: GearPal) => void;
  } | null>(null);
  const { i18n } = useTranslation();
  const { user } = useUserStore();
  const queryClient = useQueryClient();

  const isOpen = useCallback(
    (id: ModalId) => {
      return modals[id] ?? false;
    },
    [modals]
  );

  const open = useCallback((id: ModalId) => {
    setModals((prev) => ({ ...prev, [id]: true }));
  }, []);

  const close = useCallback((id: ModalId) => {
    setModals((prev) => ({ ...prev, [id]: false }));
  }, []);

  const toggle = useCallback((id: ModalId) => {
    setModals((prev) => ({ ...prev, [id]: !(prev[id] ?? false) }));
  }, []);

  // Query per il profilo quando il modal editProfile è aperto
  const isEditProfileOpen = isOpen("editProfile");
  const { data: profileData } = useQuery<User>({
    queryKey: ["profile", i18n.language],
    queryFn: () => getProfile(i18n.language),
    enabled: Boolean(user) && isEditProfileOpen,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const handleProfileUpdate = useCallback(
    (updated: User) => {
      queryClient.invalidateQueries({ queryKey: ["profile", i18n.language] });
      queryClient.setQueryData(["profile", i18n.language], updated);
      close("editProfile");
    },
    [queryClient, i18n.language, close]
  );

  const openUserAddress = useCallback(
    (
      isEdit: boolean = false,
      palToEdit?: GearPal | null,
      onSuccess?: (pal?: GearPal) => void
    ) => {
      setUserAddressState({ isEdit, palToEdit: palToEdit || null, onSuccess });
      open("userAddress");
    },
    [open]
  );

  const closeUserAddress = useCallback(() => {
    setUserAddressState(null);
    close("userAddress");
  }, [close]);

  const handleUserAddressSuccess = useCallback(
    (updatedPal?: GearPal) => {
      queryClient.invalidateQueries({ queryKey: ["gearpals"] });
      queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });
      if (userAddressState?.onSuccess) {
        userAddressState.onSuccess(updatedPal);
      }
      closeUserAddress();
    },
    [queryClient, closeUserAddress, userAddressState]
  );

  const openContactSelector = useCallback(
    (contacts: Contact[], type: Contact["type"]) => {
      setContactSelectorState({ contacts, type });
      open("contactSelector");
    },
    [open]
  );

  const closeContactSelector = useCallback(() => {
    setContactSelectorState(null);
    close("contactSelector");
  }, [close]);

  const formatContactUrl = useCallback((contact: Contact): string => {
    const { type, value } = contact;

    if (type === "email") {
      return `mailto:${value}`;
    }

    if (value.startsWith("http://") || value.startsWith("https://")) {
      return value;
    }

    return `https://${value}`;
  }, []);

  const handleContactSelect = useCallback(
    (contact: Contact) => {
      const url = formatContactUrl(contact);
      window.open(url, "_blank", "noopener,noreferrer");
      closeContactSelector();
    },
    [formatContactUrl, closeContactSelector]
  );

  const value = useMemo(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      openContactSelector,
      openUserAddress,
    }),
    [isOpen, open, close, toggle, openContactSelector, openUserAddress]
  );

  const profile: User | undefined = profileData ?? user;
  const isContactSelectorOpen = isOpen("contactSelector");
  const isUserAddressOpen = isOpen("userAddress");

  return (
    <ModalsContext.Provider value={value}>
      {children}
      {profile && (
        <EditProfileModal
          isOpen={isEditProfileOpen}
          onClose={() => close("editProfile")}
          initialData={profile}
          onSuccess={handleProfileUpdate}
        />
      )}
      {contactSelectorState && (
        <ContactSelectorModal
          isOpen={isContactSelectorOpen}
          onClose={closeContactSelector}
          contacts={contactSelectorState.contacts}
          contactType={contactSelectorState.type}
          onSelect={handleContactSelect}
        />
      )}
      {userAddressState && (
        <UserAddressForm
          isOpen={isUserAddressOpen}
          onClose={closeUserAddress}
          onSubmitSuccess={handleUserAddressSuccess}
          isEdit={userAddressState.isEdit}
          palToEdit={userAddressState.palToEdit}
        />
      )}
    </ModalsContext.Provider>
  );
};

export const useModal = (
  id: ModalId
): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
} => {
  const ctx = useContext(ModalsContext);
  if (!ctx) {
    throw new Error("useModal deve essere usato dentro ModalsProvider");
  }
  return {
    isOpen: ctx.isOpen(id),
    open: () => ctx.open(id),
    close: () => ctx.close(id),
    toggle: () => ctx.toggle(id),
  };
};

// Hook specifico per retrocompatibilità e facilità d'uso
export const useEditProfile = () => {
  return useModal("editProfile");
};

// Hook per gestire il modal dell'indirizzo
export const useUserAddress = () => {
  const ctx = useContext(ModalsContext);
  if (!ctx) {
    throw new Error("useUserAddress deve essere usato dentro ModalsProvider");
  }
  return {
    isOpen: ctx.isOpen("userAddress"),
    open: (
      isEdit: boolean = false,
      palToEdit?: GearPal | null,
      onSuccess?: (pal?: GearPal) => void
    ) => ctx.openUserAddress(isEdit, palToEdit, onSuccess),
    close: () => ctx.close("userAddress"),
  };
};
